import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import PendingEditModal from "./PendingEditModal";

const money = (n) =>
  Number(n).toLocaleString(undefined, { style: "currency", currency: "USD" });
// Row-level transaction date — compact yy-mm-dd (2026-08-20).
const fmtDateShort = (iso) => (iso ? `${iso.slice(2, 4)}-${iso.slice(5, 7)}-${iso.slice(8, 10)}` : "");

function PayLit({ txn }) {
  const pt = Number(txn.pay_tp);
  const label = txn.pay_lit || `pay_tp ${pt}`;
  if (pt === 0) return <span className="pay-lit-pending">{label}</span>;
  if (pt === 1) return <span className="pay-lit-approved">{label}</span>;
  if (pt === 2) return <span className="pay-lit-rejected">{label}</span>;
  return <span>{label}</span>;
}

const GRANT_NAMES = { TXN_ADM: "Admin", TXN_APV: "Approver", TXN_PAY: "Treasurer", TXN_INQ: "Inquiry" };

/**
 * Grant-aware PENDING worklist (sub-tab of the Transactions page).
 *
 * creator (holding TXN_APV/PAY):  own rows with pay_tp ∈ {0,2} — re-edit resets to 0
 * TXN_APV:                        others' rows with pay_tp ∈ {0,1,2} — approve/reject
 * TXN_PAY:                        any rows with pay_tp = 1 — record payment
 * TXN_ADM / SUPER:                union of all three — full edit
 */
export default function PendingTransactions({ calSeq, profile, fyRange, refreshKey, onChanged, view, onChangeView }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    if (!calSeq) return;
    setLoading(true);
    api.transactionsPending(calSeq)
      .then((r) => { setData(r); setAccessError(null); })
      .catch((err) => {
        setData({ items: [], total: 0, viewer_grants: [] });
        setAccessError(
          err?.response?.status === 403
            ? (err.response.data?.detail || "You don't have a Transactions grant.")
            : null
        );
      })
      .finally(() => setLoading(false));
  }, [calSeq, refreshKey]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const viewerGrants = useMemo(() => data?.viewer_grants || [], [data]);
  const isSuper = viewerGrants.includes("SUPER");

  const roleLabel = isSuper
    ? "SUPER"
    : (viewerGrants.map((g) => GRANT_NAMES[g] || g).join(" + ") || "Viewer");

  const roleHint = useMemo(() => {
    if (isSuper || viewerGrants.includes("TXN_ADM")) return "All actionable rows from every queue — full edit.";
    const hints = [];
    if (viewerGrants.includes("TXN_APV") || viewerGrants.includes("TXN_PAY")) {
      hints.push("Your own PENDING/REJECTED requests — editing resends them to PENDING.");
    }
    if (viewerGrants.includes("TXN_APV")) hints.push("Others' pending requests — approve, reject, or annotate.");
    if (viewerGrants.includes("TXN_PAY")) hints.push("Approved requests awaiting payment — choose a payment type.");
    return hints.join(" ");
  }, [viewerGrants, isSuper]);

  return (
    <section>
      <p className="section-title">
        Pending · {roleLabel}
      </p>
      <div className="card list-card txn-card" data-testid="pending-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            {onChangeView && (
              <>
                <label className="budget-label" htmlFor="pending-view-select">View</label>
                <select
                  id="pending-view-select"
                  className="fy-select"
                  data-testid="txn-view-select"
                  value={view || "pending"}
                  onChange={(e) => onChangeView(e.target.value)}
                  title="Transactions view"
                  style={{ marginRight: 12 }}
                >
                  <option value="all">ALL</option>
                  <option value="pending">PENDING</option>
                </select>
              </>
            )}
            <span className="count" data-testid="pending-count">
              {total === 0 ? "0 results" : `${total} txn${total === 1 ? "" : "s"}`}
            </span>
            <span className="muted" data-testid="pending-hint" style={{ fontSize: 12 }}>
              {roleHint}
            </span>
          </div>
        </div>

        <div className="list-scroll txn-scroll">
          {(() => {
            if (loading && !items.length) {
              return <div className="loading-row"><span className="spinner" /> Loading…</div>;
            }
            if (accessError) {
              return (
                <div className="empty" data-testid="pending-access-error">
                  <span className="big" aria-hidden>⚠</span>
                  {accessError}
                </div>
              );
            }
            if (items.length === 0) {
              return (
                <div className="empty">
                  <span className="big" aria-hidden>∅</span>
                  Nothing in your pending queue.
                </div>
              );
            }
            return (
              <table className="list-table list-table-compact txn-table txn-table-compact" data-testid="pending-table">
                <thead>
                  <tr className="list-head-labels txn-head-labels">
                    <th>Pay Date</th>
                    <th>Status</th>
                    <th>Check #</th>
                    <th>Payee</th>
                    <th>Code</th>
                    <th>Note</th>
                    <th>Created By</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr
                      key={t.txn_seq}
                      data-testid={`pending-row-${t.txn_seq}`}
                      onClick={() => setSelected(t)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{fmtDateShort(t.pay_dt)}</td>
                      <td className="pay-tp-cell"><PayLit txn={t} /></td>
                      <td className="muted">{t.txn_chk || "—"}</td>
                      <td>
                        {t.pye_nm || t.pye_id}{" "}
                        <span className="muted" style={{ fontSize: 11 }}>({t.pye_id})</span>
                      </td>
                      <td>
                        {t.codes_id}{" "}
                        <span className="muted" style={{ fontSize: 11 }}>
                          {t.codes_lit ? `· ${t.codes_lit}` : ""}
                        </span>
                      </td>
                      <td className="muted note-cell">{t.txn_note || "—"}</td>
                      <td className="muted">{t.created_by}</td>
                      <td className="amount">{money(t.txn_amt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      </div>

      {selected && fyRange && (
        <PendingEditModal
          txn={selected}
          profile={profile}
          calSeq={calSeq}
          fyRange={fyRange}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onChanged && onChanged();
          }}
        />
      )}
    </section>
  );
}
